<template>
  <AdminLayout>
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Header -->
      <div class="mb-6 flex items-center gap-3">
        <div class="p-2 bg-[#22282F]/10 rounded-lg">
          <FileBarChart class="w-7 h-7 text-[#22282F]" />
        </div>
        <div>
          <h1 class="text-2xl sm:text-3xl font-bold text-gray-900">Rapport journalier</h1>
          <p class="text-sm text-gray-500">Configuration de l'envoi automatique et génération à la demande.</p>
        </div>
      </div>

      <!-- Configuration -->
      <div class="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <div class="flex items-center justify-between mb-4">
          <h2 class="font-semibold text-gray-800">Envoi automatique</h2>
          <label class="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" v-model="config.enabled" class="w-4 h-4" />
            <span class="text-sm text-gray-600">Activé (jours ouvrés)</span>
          </label>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label class="block text-sm font-medium text-gray-600 mb-1">Destinataires (un email par ligne)</label>
            <textarea v-model="recipientsText" rows="5"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
              placeholder="nom@gouv.bj"></textarea>
          </div>
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-600 mb-1">Heure d'envoi</label>
              <input type="time" v-model="config.send_time"
                class="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              <span class="ml-2 text-xs text-gray-400">{{ config.timezone }}</span>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-600 mb-2">Jours d'envoi</label>
              <div class="flex flex-wrap gap-2">
                <button v-for="(d, i) in dayLabels" :key="i" type="button"
                  @click="toggleDay(i)"
                  :class="['px-3 py-1.5 rounded-lg text-sm border',
                    config.weekdays.includes(i)
                      ? 'bg-[#1d2a5e] text-white border-[#1d2a5e]'
                      : 'bg-white text-gray-500 border-gray-300']">
                  {{ d }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="mt-5 flex justify-end">
          <button @click="saveConfig" :disabled="saving"
            class="px-5 py-2 bg-[#1d2a5e] text-white rounded-lg text-sm hover:opacity-90 disabled:opacity-50">
            {{ saving ? 'Enregistrement…' : 'Enregistrer' }}
          </button>
        </div>
      </div>

      <!-- Génération manuelle -->
      <div class="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h2 class="font-semibold text-gray-800 mb-1">Génération manuelle</h2>
        <p class="text-sm text-gray-500 mb-4">Appliquez des filtres puis téléchargez ou envoyez le rapport.</p>

        <StatsFilterBar @change="onFilterChange" />

        <div class="mt-4 flex flex-wrap gap-3">
          <button @click="downloadReport" :disabled="busy"
            class="px-4 py-2 bg-[#1d2a5e] text-white rounded-lg text-sm hover:opacity-90 disabled:opacity-50 flex items-center gap-2">
            <Download class="w-4 h-4" /> Télécharger (.pptx)
          </button>
          <button @click="sendReport" :disabled="busy"
            class="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:opacity-90 disabled:opacity-50 flex items-center gap-2">
            <Send class="w-4 h-4" /> Envoyer maintenant
          </button>
          <span v-if="busy" class="text-sm text-gray-400 self-center">Traitement…</span>
        </div>
      </div>

      <!-- Historique -->
      <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div class="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 class="font-semibold text-gray-800">Historique des générations</h2>
          <button @click="loadRuns" class="text-sm text-[#1d2a5e] hover:underline">Actualiser</button>
        </div>
        <div class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead class="bg-gray-50 text-gray-500">
              <tr>
                <th class="text-left px-4 py-2 font-medium">Date</th>
                <th class="text-left px-4 py-2 font-medium">Type</th>
                <th class="text-left px-4 py-2 font-medium">Statut</th>
                <th class="text-left px-4 py-2 font-medium">Destinataires</th>
                <th class="text-left px-4 py-2 font-medium">Fichier</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in runs" :key="r.id" class="border-t border-gray-100">
                <td class="px-4 py-2 text-gray-600">{{ formatDate(r.created_at) }}</td>
                <td class="px-4 py-2">{{ r.triggered_by === 'auto' ? 'Automatique' : 'Manuelle' }}</td>
                <td class="px-4 py-2">
                  <span :class="['px-2 py-0.5 rounded-full text-xs',
                    r.status === 'sent' ? 'bg-emerald-100 text-emerald-700'
                    : r.status === 'error' ? 'bg-red-100 text-red-700'
                    : 'bg-gray-100 text-gray-600']">{{ statusLabel(r.status) }}</span>
                </td>
                <td class="px-4 py-2 text-gray-500">{{ (r.recipients_sent || []).length || '—' }}</td>
                <td class="px-4 py-2">
                  <a v-if="r.has_file" href="#" @click.prevent="downloadRun(r)"
                    class="text-[#1d2a5e] hover:underline">Télécharger</a>
                  <span v-else class="text-gray-300">—</span>
                </td>
              </tr>
              <tr v-if="!runs.length">
                <td colspan="5" class="px-4 py-6 text-center text-gray-400">Aucune génération pour l'instant.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </AdminLayout>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { FileBarChart, Download, Send } from 'lucide-vue-next'
import AdminLayout from '@/layouts/AdminLayout.vue'
import StatsFilterBar from '@/components/dashboard/StatsFilterBar.vue'
import { apiFetch, API_BASE_URL } from '@/utils/apiFetch'
import { useToast } from '@/utils/toast'

const toast = useToast()
const dayLabels = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']

const config = ref<any>({ enabled: false, send_time: '09:00', timezone: 'Africa/Porto-Novo', weekdays: [0, 1, 2, 3, 4] })
const recipientsText = ref('')
const saving = ref(false)
const busy = ref(false)
const filters = ref<Record<string, string>>({})
const runs = ref<any[]>([])

function toggleDay(i: number) {
  const idx = config.value.weekdays.indexOf(i)
  if (idx === -1) config.value.weekdays.push(i)
  else config.value.weekdays.splice(idx, 1)
}

function statusLabel(s: string) {
  return s === 'sent' ? 'Envoyé' : s === 'error' ? 'Erreur' : 'Généré'
}
function formatDate(s: string) {
  try { return new Date(s).toLocaleString('fr-FR') } catch { return s }
}

async function loadConfig() {
  try {
    const c = await apiFetch('/api/admin/reports/config/')
    config.value = { ...c }
    recipientsText.value = (c.recipients || []).join('\n')
  } catch (e: any) {
    toast.error('Erreur', e.message || 'Configuration introuvable')
  }
}

async function saveConfig() {
  saving.value = true
  try {
    const recipients = recipientsText.value.split('\n').map(s => s.trim()).filter(Boolean)
    const c = await apiFetch('/api/admin/reports/config/', {
      method: 'PUT',
      body: JSON.stringify({
        recipients,
        enabled: config.value.enabled,
        send_time: config.value.send_time,
        timezone: config.value.timezone,
        weekdays: config.value.weekdays,
      }),
    })
    config.value = { ...c }
    recipientsText.value = (c.recipients || []).join('\n')
    toast.success('Enregistré', 'Configuration mise à jour.')
  } catch (e: any) {
    toast.error('Erreur', e.message || 'Enregistrement impossible')
  } finally {
    saving.value = false
  }
}

function onFilterChange(params: Record<string, string>) {
  filters.value = params || {}
}

async function blobDownload(endpoint: string, method = 'POST', body?: any) {
  const resp = await fetch(`${API_BASE_URL}${endpoint}`, {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  })
  if (!resp.ok) {
    let msg = `Erreur ${resp.status}`
    try { msg = (await resp.json()).error || msg } catch { /* ignore */ }
    throw new Error(msg)
  }
  const blob = await resp.blob()
  const cd = resp.headers.get('content-disposition') || ''
  const match = cd.match(/filename="?([^"]+)"?/)
  const filename = match ? match[1] : 'rapport.pptx'
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

async function downloadReport() {
  busy.value = true
  try {
    await blobDownload('/api/admin/reports/generate/', 'POST', filters.value)
    toast.success('Rapport généré', 'Le téléchargement va démarrer.')
  } catch (e: any) {
    toast.error('Erreur', e.message || 'Génération impossible')
  } finally {
    busy.value = false
  }
}

async function sendReport() {
  busy.value = true
  try {
    const res: any = await apiFetch('/api/admin/reports/send/', {
      method: 'POST',
      body: JSON.stringify(filters.value),
    })
    if (res.errors && res.errors.length && !res.sent.length) {
      toast.error('Échec', res.errors.join(', '))
    } else {
      toast.success('Envoyé', `Rapport envoyé à ${res.sent.length} destinataire(s).`)
    }
    loadRuns()
  } catch (e: any) {
    toast.error('Erreur', e.message || 'Envoi impossible')
  } finally {
    busy.value = false
  }
}

async function downloadRun(r: any) {
  try {
    await blobDownload(`/api/admin/reports/runs/${r.id}/download/`, 'GET')
  } catch (e: any) {
    toast.error('Erreur', e.message || 'Téléchargement impossible')
  }
}

async function loadRuns() {
  try {
    const res: any = await apiFetch('/api/admin/reports/runs/')
    runs.value = res.results || []
  } catch (e: any) {
    toast.error('Erreur', e.message || 'Historique indisponible')
  }
}

onMounted(() => { loadConfig(); loadRuns() })
</script>
